import { KpiGroupCard } from "@/components/ui/KpiGroupCard";
import { formatAmountCompact } from "@/utils/expenseFormatters";
import { CashFlowKPIs } from "./interfaces";
import { formatCurrency } from "./utils";

interface KPIRowProps {
  kpis: CashFlowKPIs;
  rangeLabel: string;
}

const tip = (formula: string, example: string) => (
  <div className="space-y-1.5">
    <p className="font-semibold">Formula</p>
    <p className="text-muted-foreground whitespace-pre-line">{formula}</p>
    <p className="font-semibold mt-2">Example</p>
    <p className="text-muted-foreground whitespace-pre-line">{example}</p>
  </div>
);

const lockedTip = (
  <div className="space-y-1">
    <p className="font-semibold">Requires Bank Connection</p>
    <p className="text-muted-foreground">Connect your bank accounts to enable this metric.</p>
  </div>
);

export const KPIRow = ({ kpis, rangeLabel }: KPIRowProps) => {
  const hasBankData = !!kpis.hasBankData;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
      <KpiGroupCard items={[
        {
          label: kpis.cashOnHand.name,
          value: hasBankData ? formatCurrency(kpis.cashOnHand.value) : '--',
          compactValue: hasBankData ? formatAmountCompact(kpis.cashOnHand.value) : '--',
          sub: "Bank accounts balance",
          valueClass: hasBankData && kpis.cashOnHand.value > 0 ? 'text-green-600' : '',
          live: true,
          disabled: !hasBankData,
          tip: hasBankData
            ? tip(kpis.cashOnHand.formula, "Checking $75k + Savings $25k − Credit card $10k\n→ Cash Balance = $90,000")
            : lockedTip,
        },
        {
          label: kpis.netCashFlowMonth.name,
          value: hasBankData ? formatCurrency(kpis.netCashFlowMonth.value) : '--',
          compactValue: hasBankData ? formatAmountCompact(kpis.netCashFlowMonth.value) : '--',
          sub: "Inflows minus outflows",
          valueClass: hasBankData
            ? (kpis.netCashFlowMonth.value > 0 ? 'text-green-600' : kpis.netCashFlowMonth.value < 0 ? 'text-red-600' : '')
            : '',
          period: rangeLabel,
          disabled: !hasBankData,
          tip: hasBankData
            ? tip(kpis.netCashFlowMonth.formula, "Bank inflows $120k − outflows $95k\n→ Net Cash Flow = $25,000")
            : lockedTip,
        },
        {
          label: kpis.cashCollected.name,
          value: formatCurrency(kpis.cashCollected.value),
          compactValue: formatAmountCompact(kpis.cashCollected.value),
          sub: "Payments received",
          valueClass: kpis.cashCollected.value > 0 ? 'text-green-600' : '',
          period: rangeLabel,
          tip: tip(kpis.cashCollected.formula, "Bank inflows $120k (excl. transfers & loans)\n→ Cash Inflows = $120,000"),
        },
      ]} />
      <KpiGroupCard items={[
        {
          label: kpis.cashExpenses.name,
          value: formatCurrency(kpis.cashExpenses.value),
          compactValue: formatAmountCompact(kpis.cashExpenses.value),
          sub: "Vendor payments, payroll & expenses",
          valueClass: kpis.cashExpenses.value > 0 ? 'text-red-600' : '',
          period: rangeLabel,
          tip: tip(kpis.cashExpenses.formula, "Vendor payments $45k + Payroll $30k + Subscriptions $5k\n→ Cash Outflows = $80,000"),
        },
        {
          label: kpis.operatingCashFlow.name,
          value: formatCurrency(kpis.operatingCashFlow.value),
          compactValue: formatAmountCompact(kpis.operatingCashFlow.value),
          sub: "Collected minus expenses",
          valueClass: kpis.operatingCashFlow.value > 0 ? 'text-green-600' : kpis.operatingCashFlow.value < 0 ? 'text-red-600' : '',
          period: rangeLabel,
          tip: tip(kpis.operatingCashFlow.formula, "Cash Collected $70k − Cash Expenses $65k\n→ Operating CF = $5,000"),
        },
        {
          label: kpis.burnRate.name,
          value: hasBankData
            ? (kpis.burnRate.value === 0 ? 'Cash Flow +' : `${formatCurrency(kpis.burnRate.value)}${kpis.burnRate.unit ? ' ' + kpis.burnRate.unit : ''}`)
            : '--',
          compactValue: hasBankData && kpis.burnRate.value !== 0
            ? `${formatAmountCompact(kpis.burnRate.value)}${kpis.burnRate.unit ? ' ' + kpis.burnRate.unit : ''}`
            : undefined,
          sub: "Monthly spend rate",
          valueClass: hasBankData && kpis.burnRate.value > 0 ? 'text-red-600' : '',
          period: rangeLabel,
          disabled: !hasBankData,
          tip: hasBankData
            ? tip(kpis.burnRate.formula, "|Net Cash Flow| $60k ÷ 3 months\n→ Burn Rate = $20,000/mo\n(only shown when spending > income)")
            : lockedTip,
        },
      ]} />
    </div>
  );
};
